import { Button, FormGroup, Input, Label } from '@/components'
import { FlexWrapper } from '@/components/layout/flex/flexWrapper'
import { useAxios } from '@/context/axiosContext'
import { useDialog } from '@/context/dialogContext'
import React, { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { PreviewIcon } from '../feedbackItem'

interface PostData {
  file?: any
  fileSeq?: string
}

interface FileUploadProps {
  onDeletedFile: (fileSeqs: number[], fileNames: string[]) => void
  onUploadedFile: (
    fileSeqs: number[],
    fileNames: string[],
    fileSources: string[],
  ) => void
  resetUploadTrigger: boolean
  onUploadComplete?: (newFileSeq: number) => void
  fileOrgName?: string
  fileSeq?: number // 기존 파일 시퀀스
  fileSource?: string // 기존 파일 소스
  onFileSelectionChange?: (hasSelectedFiles: boolean) => void
  // 파일 상태 체크를 위한 새로운 prop 추가
  onFileStatusChange?: (hasValidFile: boolean) => void
  isMobile?: boolean
}

const FileUpload: React.FC<FileUploadProps> = ({
  onDeletedFile,
  onUploadedFile,
  resetUploadTrigger,
  onUploadComplete,
  fileOrgName,
  fileSeq,
  fileSource,
  onFileSelectionChange,
  onFileStatusChange,
  isMobile,
}) => {
  const axios = useAxios()
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<PostData>({
    defaultValues: {},
  })
  const { showAlert, showConfirm } = useDialog()

  // 파일 업로드 관련 상태
  const [uploadFile, setUploadFile] = useState<any | null>(null)
  const [currentUploadedFile, setCurrentUploadedFile] = useState<{
    seq: number
    name: string
    source: string
  } | null>(null)
  const [disabled, setDisabled] = useState(false)

  // 삭제/업로드 배열 관리
  const [deleteQueue, setDeleteQueue] = useState<number[]>([])
  const [uploadQueue, setUploadQueue] = useState<number[]>([])
  const [deleteFileNames, setDeleteFileNames] = useState<string[]>([])
  const [uploadFileNames, setUploadFileNames] = useState<string[]>([])
  const [deleteFileSources, setDeleteFileSources] = useState<string[]>([])
  const [uploadFileSources, setUploadFileSources] = useState<string[]>([])
  const [hasSelectedFile, setHasSelectedFile] = useState(false)

  // 기존 파일이 삭제 대기열에 있는지 확인
  const isExistingFileDeleted = fileSeq && deleteQueue.includes(fileSeq)

  // 현재 유효한 파일이 있는지 체크하는 함수
  const checkValidFileExists = (): boolean => {
    // 1. 새로 업로드된 파일이 있는 경우
    if (currentUploadedFile) {
      return true
    }

    // 2. 기존 파일이 있고 삭제 예정이 아닌 경우
    if (fileSeq && fileSeq > 0 && !isExistingFileDeleted) {
      return true
    }

    return false
  }

  // 파일 상태가 변경될 때마다 상위 컴포넌트에 알림
  useEffect(() => {
    const hasValidFile = checkValidFileExists()
    onFileStatusChange?.(hasValidFile)
  }, [currentUploadedFile, deleteQueue, fileSeq])

  useEffect(() => {
    onFileSelectionChange?.(hasSelectedFile)
  }, [hasSelectedFile])

  // 업로드 대기열에 추가하는 함수
  const addToUploadQueue = (
    fileSeq: number,
    fileName: string,
    fileSource: string,
  ) => {
    setUploadQueue([fileSeq]) // 한 번에 하나씩만 업로드
    setUploadFileNames([fileName])
    setUploadFileSources([fileSource])

    const seqArray = [fileSeq]
    const nameArray = [fileName]
    const sourceArray = [fileSource]

    // 상위 컴포넌트에 업로드 정보 전달
    onUploadedFile(seqArray, nameArray, sourceArray)
  }

  // 삭제 대기열에 추가하는 함수
  const addToDeleteQueue = (
    fileSeq: number,
    fileName?: string,
    fileSource?: string,
  ) => {
    const newQueue = [...deleteQueue, fileSeq]
    const newFileNames = fileName
      ? [...deleteFileNames, fileName]
      : deleteFileNames
    const newFileSources = fileSource
      ? [...deleteFileSources, fileSource]
      : deleteFileSources

    // 상태 업데이트
    setDeleteQueue(newQueue)
    if (fileName) {
      setDeleteFileNames(newFileNames)
    }
    if (fileSource) {
      setDeleteFileSources(newFileSources)
    }

    // 상위 컴포넌트에 삭제 정보 전달
    onDeletedFile(newQueue, newFileNames)
  }

  const uploadImage: SubmitHandler<PostData> = async (data) => {
    if (!uploadFile) {
      showAlert({
        type: 'error',
        title: '업로드할 파일을 선택해 주세요.',
      })
      return
    }

    const maxFileSize = 10 * 1024 * 1024

    // 파일 크기 체크
    if (uploadFile.size > maxFileSize) {
      showAlert({
        title: '10MB 이상의 파일은 업로드할 수 없어요.',
        message: `파일 크기가 ${(uploadFile.size / (1024 * 1024)).toFixed(2)}MB 에요.`,
      })
      return
    }

    try {
      // 1. 기존 파일이 있으면 삭제 대기열에 추가
      if (fileSeq && fileSeq > 0) {
        addToDeleteQueue(fileSeq, fileOrgName, fileSource)
      }

      // 3. 현재 세션에서 업로드한 파일이 있으면 삭제 대기열에 추가
      if (currentUploadedFile) {
        addToDeleteQueue(
          currentUploadedFile.seq,
          currentUploadedFile.name,
          currentUploadedFile.source,
        )
      }

      // 4. 새 파일 업로드
      const formData = new FormData()
      formData.append('file', uploadFile)

      const response = await axios.post('/feedback/fileUpload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      if (response.status === 200) {
        const { fileSeq: newFileSeq, fileNm } = response.data.data
        const newFileSource = 'UPLOAD' // 업로드 파일의 source는 항상 'UPLOAD'

        // 5. 성공 후 상태 업데이트
        setDisabled(true)
        setCurrentUploadedFile({
          seq: newFileSeq,
          name: fileNm,
          source: newFileSource,
        })

        // 6. 업로드 대기열에 추가
        addToUploadQueue(newFileSeq, fileNm, newFileSource)

        if (onUploadComplete) {
          onUploadComplete(newFileSeq)
        }

        handleUploadComplete()

        showAlert({
          title: '이미지가 업로드됐어요.',
        })
      } else {
        throw new Error('Upload failed')
      }
    } catch (error) {
      showAlert({
        type: 'error',
        title: '업로드에 실패했어요. 다시 시도해 주세요.',
      })
    }
  }

  const handleFileAction = () => {
    if (disabled) {
      // 삭제 동작
      if (currentUploadedFile) {
        // 새로운 삭제 정보 계산
        const newDeleteQueue = [...deleteQueue, currentUploadedFile.seq]
        const newDeleteFileNames = [
          ...deleteFileNames,
          currentUploadedFile.name,
        ]

        // 상태 업데이트
        setDeleteQueue(newDeleteQueue)
        setDeleteFileNames(newDeleteFileNames)
        if (currentUploadedFile.source) {
          setDeleteFileSources((prev) => [...prev, currentUploadedFile.source])
        }

        // 업로드 관련 상태에서 완전히 제거
        setUploadQueue([]) // 현재 세션에서는 하나씩만 업로드하므로 전체 초기화
        setUploadFileNames([])
        setUploadFileSources([])

        // 상위 컴포넌트에 삭제 정보 전달 (전체 삭제 목록)
        onDeletedFile(newDeleteQueue, newDeleteFileNames)
      }

      // UI 상태 초기화
      setUploadFile(null)
      setCurrentUploadedFile(null)
      setDisabled(false)

      // 추가: 파일 삭제 시 선택된 파일 상태도 false로 설정
      setHasSelectedFile(false)

      const fileInput = document.getElementById('file') as HTMLInputElement
      if (fileInput) {
        fileInput.value = ''
      }
    } else {
      // 업로드 동작
      handleSubmit(uploadImage)()
    }
  }

  // FileUpload 컴포넌트에서 업로드 완료 시 호출해야 할 로직
  const handleUploadComplete = () => {
    // 업로드가 완료되면 선택된 파일 상태를 false로 변경
    onFileSelectionChange?.(false)
    // 기존 onUploadedFile 호출...
  }

  // 파일 유효성 검사 함수 (외부에서 호출 가능하도록 노출)
  const validateFileExists = (): { isValid: boolean; message?: string } => {
    const hasValidFile = checkValidFileExists()

    if (!hasValidFile) {
      return {
        isValid: false,
        message: '파일을 업로드해 주세요.',
      }
    }

    return { isValid: true }
  }

  // 초기화 트리거 처리
  useEffect(() => {
    if (resetUploadTrigger) {
      setUploadFile(null)
      setCurrentUploadedFile(null)
      setDisabled(false)
      setDeleteQueue([])
      setUploadQueue([])
      setDeleteFileNames([])
      setUploadFileNames([])
      setDeleteFileSources([])
      setUploadFileSources([])

      const fileInput = document.getElementById('file') as HTMLInputElement
      if (fileInput) {
        fileInput.value = ''
      }
    }
  }, [resetUploadTrigger])

  // 컴포넌트 인스턴스를 통해 유효성 검사 함수 노출
  React.useImperativeHandle(React.createRef(), () => ({
    validateFileExists,
  }))

  return (
    <>
      <form action="#" onSubmit={handleSubmit(uploadImage)} noValidate>
        <div
          style={{
            display: 'flex',
            justifyItems: 'center',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div style={{ marginRight: '5px' }}>
            <Input
              {...register('file', {})}
              id="file"
              name="file"
              type="file"
              disabled={disabled || resetUploadTrigger}
              onChange={(e: any) => {
                const selectedFiles = e.target.files
                const hasFiles = selectedFiles && selectedFiles.length > 0

                if (hasFiles) {
                  setUploadFile(selectedFiles[0])
                } else {
                  setUploadFile(null)
                }

                setHasSelectedFile(hasFiles) // 이것만 해도 useEffect에서 상위 알림 수행됨
              }}
            />
          </div>
          <Button color="neverbe" onClick={handleFileAction}>
            {disabled ? '삭제' : '업로드'}
          </Button>

          {/* 파일명 표시 - 개선된 버전 */}
          <div style={{ marginRight: '10px' }} />
          {!isMobile && (
            <>
              {fileOrgName && (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <PreviewIcon />
                  <span
                    style={{
                      color: isExistingFileDeleted ? '#ff4444' : '#979797',
                      textDecoration: isExistingFileDeleted
                        ? 'line-through'
                        : 'none',
                    }}
                  >
                    등록된 파일명 : {fileOrgName}
                  </span>
                </div>
              )}
            </>
          )}
          {/* 파일이 없을 때 경고 메시지 표시 */}
          {/* {!checkValidFileExists() && (
            <div
              style={{
                marginLeft: '10px',
                color: '#ff4444',
                fontSize: '14px',
                fontWeight: 'bold',
              }}
            >
              ⚠️ 등록된 파일이 없습니다
            </div>
          )} */}

          {/* 개발 모드에서 디버그 정보 표시 */}
          {/* {process.env.NODE_ENV === 'development' && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                marginTop: '10px',
                fontSize: '12px',
                color: '#666',
                background: '#f5f5f5',
                padding: '10px',
                borderRadius: '4px',
                border: '1px solid #ddd',
              }}
            >
              <div>삭제 대기열: {deleteQueue.join(', ')}</div>
              <div>업로드 대기열: {uploadQueue.join(', ')}</div>
              <div>삭제 파일명: {deleteFileNames.join(', ')}</div>
              <div>업로드 파일명: {uploadFileNames.join(', ')}</div>
              <div>삭제 파일 소스: {deleteFileSources.join(', ')}</div>
              <div>업로드 파일 소스: {uploadFileSources.join(', ')}</div>
              <div>
                기존 파일 삭제 예정: {isExistingFileDeleted ? 'Yes' : 'No'}
              </div>
              <div>
                유효한 파일 존재: {checkValidFileExists() ? 'Yes' : 'No'}
              </div>
            </div>
          )} */}
        </div>
      </form>
    </>
  )
}

export default FileUpload
